import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useApiQuery } from '../hooks/useApi';
import { Card } from '../components/ui';
import { ApiClient } from '../services/apiClient';
import type { EstadisticasDashboard } from '../types';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n/i18n';

// ===============================
// ICONOS
// ===============================

const ReportsIcon = () => (
  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const MachinesIcon = () => (
  <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
    <path d="M3.375 4.5C2.339 4.5 1.5 5.34 1.5 6.375V13.5h12V6.375c0-1.036-.84-1.875-1.875-1.875h-8.25ZM13.5 15h-12v2.625c0 1.035.84 1.875 1.875 1.875h.375a3 3 0 1 1 6 0h3a.75.75 0 0 0 .75-.75V15Z" />
    <path d="M8.25 19.5a1.5 1.5 0 1 0-3 0 1.5 1.5 0 0 0 3 0ZM15.75 6.75a.75.75 0 0 0-.75.75v11.25c0 .087.015.17.042.248a3 3 0 0 1 5.958.464c.853-.175 1.522-.935 1.464-1.883a18.659 18.659 0 0 0-3.732-10.104 1.837 1.837 0 0 0-1.47-.725H15.75Z" />
    <path d="M19.5 19.5a1.5 1.5 0 1 0-3 0 1.5 1.5 0 0 0 3 0Z" />
  </svg>
);

// Iconos de componentes - UsersIcon removido

const AreaIcon = () => (
  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
  </svg>
);

const HoursIcon = () => (
  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const LengthIcon = () => (
  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
  </svg>
);

// ===============================
// COMPONENTE DE TARJETA SIMPLE
// ===============================

interface SimpleCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon?: React.ReactNode;
  color?: 'blue' | 'green' | 'yellow' | 'red';
}

const SimpleCard: React.FC<SimpleCardProps> = ({ 
  title, 
  value, 
  subtitle, 
  icon, 
  color = 'blue' 
}) => {
  const colorClasses = {
    blue: 'from-blue-500 to-blue-600',
    green: 'from-green-500 to-green-600',
    yellow: 'from-yellow-500 to-yellow-600',
    red: 'from-red-500 to-red-600'
  };

  return (
    <Card className="hover:shadow-lg transition-all duration-200 border-0 shadow-md" padding="lg">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
          <p className="text-3xl font-bold text-gray-900 mb-1">{value}</p>
          {subtitle && (
            <p className="text-xs text-gray-500">{subtitle}</p>
          )}
        </div>
        {icon && (
          <div className={`p-3 rounded-xl bg-gradient-to-r ${colorClasses[color]} shadow-lg`}>
            <div className="text-white text-2xl">{icon}</div>
          </div>
        )}
      </div>
    </Card>
  );
};

// ===============================
// COMPONENTE PRINCIPAL
// ===============================

export const DashboardView: React.FC = () => {
  const { usuario, hasPermission } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Verificar si puede acceder a administración (cualquiera de los permisos administrativos)
  const puedeAccederAdmin = [
    'operadores.ver', 'faenas.ver', 'predios.ver', 'rodales.ver', 
    'insumos.ver', 'maquinas.ver', 'items_chequeo.ver', 
    'maquinas.administrar', 'operadores.administrar'
  ].some(permiso => hasPermission(permiso));

  // 🏢 MULTITENANT: Usar DashboardService que ya está corregido
  const {
    data: statsReportes,
    isLoading: loadingReportes,
    error: errorReportes
  } = useApiQuery<EstadisticasDashboard>(
    ['dashboard', 'estadisticas'], // 🔄 CAMBIAR KEY para invalidar cache
    async (): Promise<EstadisticasDashboard> => {

      // 🏢 MULTITENANT: Usar el servicio corregido
      const response = await ApiClient.get('/dashboard/estadisticas');


      
      // 🐛 FIX: ApiClient.get() ya devuelve response.data del axios
      // Los datos del backend están directamente en response

      
      return response as EstadisticasDashboard;
    },
    {
      // 🔧 DEBUG: Opciones de query
      enabled: true,


    }
  );

  // Función para determinar valor y estado de la primera tarjeta
  const getTotalReportesDisplay = () => {

    
    if (loadingReportes) {
      return { value: '--', subtitle: t('common.loading') };
    }
    if (errorReportes) {

      return { value: t('common.error'), subtitle: t('errors.load_error') };
    }
    if (statsReportes) {

      
      // 🐛 FIX: Usar total_reportes (todos los reportes de la empresa)
      const reportesMes = statsReportes?.total_reportes || 0;
      
      return { 
        value: reportesMes, 
        subtitle: `${statsReportes?.reportes_pendientes || 0} ${t('dashboard.pending_label')}`
      };
    }
    return { value: '--', subtitle: t('common.no_data') };
  };

  // Función para determinar valor y estado de la segunda tarjeta
  const getTotalMaquinasDisplay = () => {
    if (loadingReportes) {
      return { value: '--', subtitle: t('common.loading') };
    }
    if (errorReportes) {
      return { value: t('common.error'), subtitle: t('errors.load_error') };
    }
    if (statsReportes) {
      // 🐛 FIX: Usar total_maquinas en lugar de maquinas_activas
      return { 
        value: statsReportes?.total_maquinas || 0, 
        subtitle: t('dashboard.active_machines_month')
      };
    }
    return { value: '--', subtitle: t('common.no_data') };
  };

  // Función para determinar valor y estado de la tercera tarjeta
  const getHorasTrabajadasDisplay = () => {
    if (loadingReportes) {
      return { value: '--', subtitle: t('common.loading') };
    }
    if (errorReportes) {
      return { value: t('common.error'), subtitle: t('errors.load_error') };
    }
    if (statsReportes) {
      const horas = statsReportes?.horas_trabajadas_mes || 0;
      return { 
        value: horas.toFixed(1), 
        subtitle: t('dashboard.hours_month')
      };
    }
    return { value: '--', subtitle: t('common.no_data') };
  };

  // Función para determinar valor y estado de la cuarta tarjeta
  const getAreaTrabajadasDisplay = () => {
    if (loadingReportes) {
      return { value: '--', subtitle: t('common.loading') };
    }
    if (errorReportes) {
      return { value: t('common.error'), subtitle: t('errors.load_error') };
    }
    if (statsReportes) {
      // 🐛 FIX: Usar volumen_mes_procesado en lugar de area_trabajada_mes
      const area = statsReportes?.volumen_mes_procesado || 0;
      return { 
        value: area.toFixed(1), 
        subtitle: t('dashboard.hectares_month')
      };
    }
    return { value: '--', subtitle: t('common.no_data') };
  };

  // ✅ NUEVA: Función para determinar valor y estado de la quinta tarjeta (longitud)
  const getLongitudMesDisplay = () => {
    if (loadingReportes) {
      return { value: '--', subtitle: t('common.loading') };
    }
    if (errorReportes) {
      return { value: t('common.error'), subtitle: t('errors.load_error') };
    }
    if (statsReportes) {
      const longitud = statsReportes?.longitud_mes_metros || 0;
      return { 
        value: (longitud / 1000).toFixed(2), // Convertir a kilómetros
        subtitle: t('dashboard.km_month')
      };
    }
    return { value: '--', subtitle: t('common.no_data') };
  };

  const totalReportesDisplay = getTotalReportesDisplay();
  const totalMaquinasDisplay = getTotalMaquinasDisplay();
  const horasTrabajadasDisplay = getHorasTrabajadasDisplay();
  const areaTrabajadasDisplay = getAreaTrabajadasDisplay();
  const longitudMesDisplay = getLongitudMesDisplay(); // ✅ NUEVA variable

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('dashboard.title')}</h1>
          <p className="text-gray-600 mt-1">
            {t('dashboard.welcome')}, {usuario?.nombre_completo || usuario?.username}
          </p>
        </div>
        <div className="text-right">
          <div className="flex items-center space-x-4">
            {puedeAccederAdmin && (
              <button
                onClick={() => navigate('/admin')}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center space-x-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span>{t('admin.title')}</span>
              </button>
            )}
            <div className="text-right">
              <p className="text-sm text-gray-500">{t('dashboard.last_update')}</p>
              <p className="text-sm font-medium text-gray-900">
                {new Date().toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Período actual */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center justify-center">
          <div className="text-center">
            <p className="text-sm text-blue-600 font-medium">
              {t('dashboard.period_label')}: {new Date().toLocaleDateString(i18n.language === 'pt-BR' ? 'pt-BR' : 'es-CL', { month: 'long', year: 'numeric' }).toUpperCase()}
            </p>
            <p className="text-xs text-blue-500 mt-1">
              {t('dashboard.data_from')} {new Date().toLocaleDateString(i18n.language === 'pt-BR' ? 'pt-BR' : 'es-CL', { month: 'long' })} {t('dashboard.until_today')}
            </p>
          </div>
        </div>
      </div>

      {/* Tarjetas Principales - TODAS ESTÁTICAS POR AHORA */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <SimpleCard
          title={t('dashboard.total_reports')}
          value={totalReportesDisplay.value}
          subtitle={totalReportesDisplay.subtitle}
          icon={<ReportsIcon />}
          color="blue"
        />
        <SimpleCard
          title={t('dashboard.active_machines')}
          value={totalMaquinasDisplay.value}
          subtitle={totalMaquinasDisplay.subtitle}
          icon={<MachinesIcon />}
          color="green"
        />
        <SimpleCard
          title={t('dashboard.total_hours')}
          value={horasTrabajadasDisplay.value}
          subtitle={horasTrabajadasDisplay.subtitle}
          icon={<HoursIcon />}
          color="red"
        />
        <SimpleCard
          title={t('dashboard.total_area')}
          value={areaTrabajadasDisplay.value}
          subtitle={areaTrabajadasDisplay.subtitle}
          icon={<AreaIcon />}
          color="yellow"
        />
        <SimpleCard
          title={t('dashboard.total_length')}
          value={longitudMesDisplay.value}
          subtitle={longitudMesDisplay.subtitle}
          icon={<LengthIcon />}
          color="blue"
        />
      </div>

      {/* Mensaje de estado 
      <Card>
      <CardContent>
      <div className="text-center py-8">
      <h3 className="text-lg font-semibold text-gray-900 mb-2">
      ✅ Dashboard Completado
      </h3>
      <p className="text-gray-600">
      Las 4 tarjetas principales están funcionando correctamente con datos reales del backend.
      </p>
      <p className="text-sm text-gray-500 mt-2">
      Siguiente paso: Implementar las siguientes views del MVP
      </p>
      <div className="mt-4">
        <Button 
          onClick={() => setMostrarTestEmpresas(!mostrarTestEmpresas)}
          variant="outline"
          size="sm"
        >
          {mostrarTestEmpresas ? '🔒 Ocultar Test Empresas' : '🧪 Mostrar Test Empresas'}
        </Button>
      </div>
      </div>
      </CardContent>
      </Card> */}

      {/* Componente de prueba para empresas 
      {mostrarTestEmpresas && (
        <div className="mt-6">
          <TestEmpresas />
        </div>
      )} */}
    </div>
  );
};

export default DashboardView;
